import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { useTranslation } from 'react-i18next';

const CHECKBOX_COL_WIDTH = 50;
const FIT_WIDTH_GUTTER = 24;
const MIN_COL_WIDTH = 40;

const availableColumnWidth = (containerWidth, hasCheckbox) =>
    Math.max(0, containerWidth - (hasCheckbox ? CHECKBOX_COL_WIDTH : 0) - FIT_WIDTH_GUTTER);

const isColumnShown = (visibilityModel, field) => visibilityModel?.[field] !== false;

/**
 * Splits `available` between the visible columns by weight, so the grid never scrolls sideways. A column's
 * `fitMinWidth` is a floor (e.g. an actions column whose buttons must stay whole) and the other columns share what
 * is left; when the floors together exceed `available` they shrink in proportion.
 */
export const computeFittedWidths = (
    columns, visibilityModel, containerWidth, preferredWidths = {}, hasCheckbox = true, availableOverride = null,
) => {
    const available = availableOverride ?? availableColumnWidth(containerWidth, hasCheckbox);
    const visible = (columns || []).filter((col) => isColumnShown(visibilityModel, col.field));
    if (!visible.length || available <= 0) return null;

    const weightOf = (col) => {
        const preferred = preferredWidths?.[col.field];
        if (preferred > 0) return preferred;
        return Math.max(1, col.minWidth || col.width || 80);
    };
    const floorsTotal = visible.reduce((sum, col) => sum + Math.max(0, col.fitMinWidth || 0), 0);
    const floorScale = floorsTotal > available ? available / floorsTotal : 1;
    const floorOf = (col) => Math.floor(Math.max(0, col.fitMinWidth || 0) * floorScale);

    const fixed = {};
    let free = visible;
    for (;;) {
        const freeAvailable = available - Object.values(fixed).reduce((sum, width) => sum + width, 0);
        const freeWeight = free.reduce((sum, col) => sum + weightOf(col), 0);
        const short = free.filter((col) => floorOf(col) > (weightOf(col) / freeWeight) * freeAvailable);
        if (!short.length) break;
        short.forEach((col) => { fixed[col.field] = floorOf(col); });
        free = free.filter((col) => fixed[col.field] == null);
        if (!free.length) break;
    }

    const widths = { ...fixed };
    const freeAvailable = Math.max(0, available - Object.values(fixed).reduce((sum, width) => sum + width, 0));
    const freeWeight = free.reduce((sum, col) => sum + weightOf(col), 0);
    let used = 0;
    free.forEach((col, index) => {
        const remaining = freeAvailable - used;
        const share = index === free.length - 1
            ? remaining
            : Math.min(remaining, Math.floor((weightOf(col) / freeWeight) * freeAvailable));
        widths[col.field] = Math.max(1, share);
        used += widths[col.field];
    });

    return widths;
};

/**
 * Width left for the data columns inside the rendered grid: its borders, a reserved vertical scrollbar and the
 * real checkbox column are taken off. Null until the grid has measured itself.
 */
const measuredAvailableWidth = (apiRef, containerWidth, hasCheckbox) => {
    const api = apiRef?.current;
    const dims = api?.getRootDimensions?.();
    if (!dims?.isReady || !(dims.root?.width > 0) || !(dims.viewportOuterSize?.width > 0)) return null;
    const chrome = dims.root.width - dims.viewportOuterSize.width;
    const scrollbar = dims.scrollbarSize || 0;
    const checkbox = hasCheckbox ? (api.getColumn?.('__check__')?.computedWidth || CHECKBOX_COL_WIDTH) : 0;
    return Math.max(0, Math.floor(containerWidth - chrome - scrollbar - checkbox - 2));
};

const widthsDiffer = (previous, next, columns, visibilityModel) => (columns || []).some((col) => {
    if (!isColumnShown(visibilityModel, col.field)) return false;
    return Math.abs((previous?.[col.field] || 0) - (next?.[col.field] || 0)) > 1;
});

const readJson = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

export function useFitTableColumns({
    storageKey,
    scope = 'default',
    columns,
    visibilityModel,
    widths,
    onPersist,
    apiRef,
    hasCheckbox = true,
    weightMode = 'saved',
    refitOnOverflow = false,
}) {
    const containerRef = useRef(null);
    const widthsRef = useRef(widths || {});
    const columnsRef = useRef(columns);
    const visibilityRef = useRef(visibilityModel);
    const onPersistRef = useRef(onPersist);
    const isResizingRef = useRef(false);
    const prevFitRef = useRef(true);
    const fitTurnedOnRef = useRef(false);
    const lastFitNonceRef = useRef(0);

    widthsRef.current = widths || {};
    columnsRef.current = columns;
    visibilityRef.current = visibilityModel;
    onPersistRef.current = onPersist;

    const fitStorageKey = `${storageKey}_fitColumns`;
    const customStorageKey = `${storageKey}_columnWidthsCustom`;

    const [fitEnabled, setFitEnabled] = useState(() => {
        try {
            const stored = localStorage.getItem(fitStorageKey);
            if (stored == null) {
                localStorage.setItem(fitStorageKey, '1');
                return true;
            }
            return stored !== '0';
        } catch {
            return true;
        }
    });
    const [fitNonce, setFitNonce] = useState(0);
    const [customScopes, setCustomScopes] = useState(() => readJson(customStorageKey));
    const [containerWidth, setContainerWidth] = useState(0);
    const [gridMeasure, setGridMeasure] = useState(0);

    const widthsCustom = !!customScopes[scope];
    if (fitEnabled && !prevFitRef.current) fitTurnedOnRef.current = true;
    prevFitRef.current = fitEnabled;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const update = () => {
            if (isResizingRef.current) return;
            setContainerWidth(Math.floor(el.clientWidth));
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const api = apiRef?.current;
        if (!api?.subscribeEvent) return undefined;
        const endResize = () => {
            if (!isResizingRef.current) return;
            isResizingRef.current = false;
            const el = containerRef.current;
            if (el) setContainerWidth(Math.floor(el.clientWidth));
        };
        const onStart = api.subscribeEvent('columnResizeStart', () => {
            isResizingRef.current = true;
            // A click on a separator without dragging never publishes columnResizeStop, so the end of the gesture
            // is taken from the document too; otherwise the container size stays ignored and the grid never refits.
            const doc = containerRef.current?.ownerDocument || document;
            const finish = () => {
                doc.removeEventListener('mouseup', finish, true);
                doc.removeEventListener('touchend', finish, true);
                window.setTimeout(endResize, 0);
            };
            doc.addEventListener('mouseup', finish, true);
            doc.addEventListener('touchend', finish, true);
        });
        const onStop = api.subscribeEvent('columnResizeStop', () => {
            isResizingRef.current = false;
        });
        const remeasure = () => setGridMeasure((n) => n + 1);
        const onResize = api.subscribeEvent('resize', remeasure);
        const onViewport = api.subscribeEvent('viewportInnerSizeChange', remeasure);
        return () => {
            onStart();
            onStop();
            onResize();
            onViewport();
        };
    }, [apiRef, containerWidth]);

    const columnSignature = (columns || [])
        .map((col) => `${col.field}:${col.minWidth || col.width || 0}:${col.fitMinWidth || 0}:${visibilityModel?.[col.field] === false ? 0 : 1}`)
        .join('|');

    useLayoutEffect(() => {
        if (!fitEnabled || containerWidth <= 0 || isResizingRef.current) return;
        const nonceChanged = lastFitNonceRef.current !== fitNonce;
        lastFitNonceRef.current = fitNonce;
        const turnedOn = fitTurnedOnRef.current || nonceChanged;
        fitTurnedOnRef.current = false;
        const available = measuredAvailableWidth(apiRef, containerWidth, hasCheckbox)
            ?? availableColumnWidth(containerWidth, hasCheckbox);
        let overflowFit = false;
        if (widthsCustom && !turnedOn) {
            if (!refitOnOverflow) return;
            // Custom widths stay until they no longer fit the container; then shrink them proportionally.
            const used = (columnsRef.current || [])
                .filter((col) => isColumnShown(visibilityRef.current, col.field))
                .reduce((sum, col) => sum + (widthsRef.current?.[col.field] || col.width || col.minWidth || 80), 0);
            if (used <= available) return;
            overflowFit = true;
        }

        const fitted = computeFittedWidths(
            columnsRef.current,
            visibilityRef.current,
            containerWidth,
            weightMode === 'column' && !overflowFit ? {} : widthsRef.current,
            hasCheckbox,
            available
        );
        if (!fitted) return;
        if (turnedOn) {
            setCustomScopes((prev) => {
                if (!prev[scope]) return prev;
                const next = { ...prev, [scope]: false };
                try { localStorage.setItem(customStorageKey, JSON.stringify(next)); } catch (_) {}
                return next;
            });
        }
        if (!widthsDiffer(widthsRef.current, fitted, columnsRef.current, visibilityRef.current)) return;
        onPersistRef.current({ ...widthsRef.current, ...fitted });
    }, [fitEnabled, containerWidth, gridMeasure, columnSignature, widthsCustom, fitNonce, scope, hasCheckbox, customStorageKey,
        weightMode, refitOnOverflow, apiRef]);

    const handleColumnWidthChange = (params) => {
        const field = params?.colDef?.field ?? params?.field;
        const width = params?.width;
        isResizingRef.current = false;
        if (!field || !(width >= MIN_COL_WIDTH)) return;
        setCustomScopes((prev) => {
            if (prev[scope]) return prev;
            const next = { ...prev, [scope]: true };
            try { localStorage.setItem(customStorageKey, JSON.stringify(next)); } catch (_) {}
            return next;
        });
        onPersistRef.current({ ...widthsRef.current, [field]: width });
    };

    const requestFit = () => {
        if (!fitEnabled) {
            setFitEnabled(true);
            try { localStorage.setItem(fitStorageKey, '1'); } catch (_) {}
        }
        setFitNonce((n) => n + 1);
    };

    const columnsWithWidths = (columns || []).map((col) => {
        const w = widths?.[col.field];
        if (w != null && w > 0) {
            return { ...col, width: w, minWidth: Math.min(MIN_COL_WIDTH, w), flex: 0 };
        }
        return col;
    });

    return {
        containerRef,
        columnsWithWidths,
        fitEnabled,
        requestFit,
        handleColumnWidthChange,
    };
}

export function FitColumnsButton({ active, onClick, height = 40 }) {
    const { t } = useTranslation();
    return (
        <Tooltip title={t('FitColumnsTooltip')}>
            <Button
                variant={active ? 'contained' : 'outlined'}
                size="small"
                startIcon={<FitScreenIcon />}
                onClick={onClick}
                aria-pressed={active}
                sx={{
                    minHeight: height,
                    height,
                    textTransform: 'none',
                    borderRadius: '10px',
                    borderColor: '#304FFF',
                    color: active ? '#ffffff' : '#304FFF',
                    fontSize: 'var(--xtram-table-view-label, 11px)',
                    fontWeight: 600,
                    background: active ? '#304FFF' : '#f5f6fd',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                        borderColor: '#304FFF',
                        background: active ? '#2538d6' : '#eaedff',
                        boxShadow: 'none',
                    },
                }}
            >
                {t('FitColumns')}
            </Button>
        </Tooltip>
    );
}
